import { useUploady } from '@rpldy/uploady'
import { AttachmentType } from 'types/graphql'

import { useMutation } from '@cedarjs/web'

import { getMd5 } from 'src/utils/helpers'
import { useUploadStatus } from 'src/components/UploadStatusIndicator/UploadStatusContext'

const CREATE_FILE_BULK_ATTACH_MUTATION = gql`
  mutation CreateFileBulkAttachMutation(
    $input: CreateFileInput!
    $attachTos: [AttachToInput!]!
    $localHash: String!
  ) {
    createFileBulkAttach(
      input: $input
      attachTos: $attachTos
      localHash: $localHash
    ) {
      id
      signedUploadUrl
      originalFilename
    }
  }
`

export const INGEST_NEW_FILE_INTO_R2R_MUTATION = gql`
  mutation IngestNewFileIntoR2rMutation($fileId: String!) {
    ingestNewFileIntoR2r(fileId: $fileId) {
      eventId
    }
  }
`

export const UPDATE_FILE_HASH = gql`
  mutation UpdateFileHash($id: String!, $ext: String!, $localHash: String!) {
    updateFileHash(id: $id, ext: $ext, localHash: $localHash) {
      id
    }
  }
`

export const UPDATE_FILE_SIZE = gql`
  mutation UpdateFileSize($id: String!, $ext: String!, $localHash: String!) {
    updateFileSize(id: $id, ext: $ext, localHash: $localHash) {
      id
    }
  }
`

export const DELETE_INVALID_FILE = gql`
  mutation DeleteInvalidFile($id: String!) {
    deleteInvalidFile(id: $id) {
      id
    }
  }
`

const useUploadFile = () => {
  const [createFileBulkAttach] = useMutation(CREATE_FILE_BULK_ATTACH_MUTATION)
  const { upload } = useUploady()
  const { addUpload, updateUpload } = useUploadStatus()

  const handleUpload = async (
    file: File,
    attachables: { id: string; type: AttachmentType }[] = [],
    skipUploadPopover = false
  ): Promise<string> => {
    const localHash = await getMd5(file)

    // Add to our upload tracking system
    let uploadId
    if (!skipUploadPopover) {
      uploadId = localHash + file.name
      addUpload(uploadId, file.name)
    }

    return new Promise((resolve, reject) => {
      createFileBulkAttach({
        variables: {
          input: {
            originalFilename: file.name,
          },
          attachTos: attachables.map((attachable) =>
            attachable.id && attachable.type ? attachable : undefined
          ),
          localHash: localHash,
        },
        onError: (error) => {
          if (!skipUploadPopover) {
            updateUpload(uploadId, {
              status: 'error',
              error: error.graphQLErrors[0].extensions?.originalError?.message,
            })
          }
          reject(error)
        },
        onCompleted: async (data) => {
          const fileId = data.createFileBulkAttach.id
          if (
            data.createFileBulkAttach.signedUploadUrl === 'File already exists'
          ) {
            if (!skipUploadPopover) {
              updateUpload(uploadId, { status: 'success', attached: true })
            }
            resolve(data.createFileBulkAttach.id)
            return
          }

          upload(file, {
            destination: {
              url: data.createFileBulkAttach.signedUploadUrl,
              headers: {
                'Access-Control-Allow-Origin': '*', // Required for CORS support to work
              },
              method: 'PUT',
            },
            params: {
              fileId,
              localHash,
              resolve,
              reject,
              uploadId,
            },
            sendWithFormData: true,
          })
        },
      })
    })
  }

  return handleUpload
}

export default useUploadFile
